/* eslint-disable no-unused-vars */
import Cookies from "universal-cookie";
import jwtDecode from "jwt-decode";
import axios from "axios";
import constants from "../constants/constants";
import { APIs } from "./worker";

const cookies = new Cookies();

/**
 * Reads cookies from the current domain (works on .innofulfill.com subdomain)
 * Reads actual cookies: id_token, user_email, platform_role, refresh_token, token_type
 * Falls back to localStorage if cookies are not accessible (e.g., on localhost)
 * @returns {Object|null} Cookie data or null if not found
 */
export const readAuthCookies = () => {
  try {
    // Debug: Log all cookies

    // Read the actual cookies from the subdomain
    const idToken = cookies.get("id_token");
    const userEmail = cookies.get("user_email");
    const platformRole = cookies.get("platform_role");
    const refreshToken = cookies.get("refresh_token");
    const tokenType = cookies.get("token_type") || "Bearer";


    // If we have id_token and user_email, we have valid auth data
    if (idToken && userEmail) {
      return {
        id_token: idToken,
        accessToken: idToken, // Alias for compatibility
        name: idToken, // Alias for compatibility with existing code
        email: userEmail,
        username: userEmail, // Alias for compatibility
        platform_role: platformRole,
        refresh_token: refreshToken,
        token_type: tokenType,
      };
    }

    // Fallback: Try reading from document.cookie directly (for cross-domain scenarios)
    const getCookieValue = (name) => {
      const cookieString = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${name}=`));

      if (cookieString) {
        return decodeURIComponent(cookieString.split("=").slice(1).join("="));
      }
      return null;
    };

    const directIdToken = getCookieValue("id_token");
    const directUserEmail = getCookieValue("user_email");
    const directPlatformRole = getCookieValue("platform_role");
    const directRefreshToken = getCookieValue("refresh_token");
    const directTokenType = getCookieValue("token_type") || "Bearer";

    if (directIdToken && directUserEmail) {
      return {
        id_token: directIdToken,
        accessToken: directIdToken,
        name: directIdToken,
        email: directUserEmail,
        username: directUserEmail,
        platform_role: directPlatformRole,
        refresh_token: directRefreshToken,
        token_type: directTokenType,
      };
    }

    // If still not found and we're on localhost, try localStorage as fallback
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      const userinfo = localStorage.getItem("userinfo");
      if (userinfo) {
        try {
          const parsed = JSON.parse(userinfo);
          if (parsed?.accessToken && parsed?.username) {
            return {
              id_token: parsed.accessToken,
              accessToken: parsed.accessToken,
              name: parsed.accessToken,
              email: parsed.username,
              username: parsed.username,
              platform_role: parsed.platform_role,
              refresh_token: parsed.refresh_token,
              token_type: parsed.token_type || "Bearer",
            };
          }
        } catch (e) {
          console.error("Error parsing userinfo from localStorage:", e);
        }
      }
    }

    return null;
  } catch (error) {
    console.error("Error reading auth cookies:", error);
    return null;
  }
};

/**
 * Decodes JWT token to extract user information
 * @param {string} token - JWT access token
 * @returns {Object|null} Decoded token data or null
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;
    return jwtDecode(token);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Calculates expiration time based on token or provided expiresIn
 * @param {string} token - JWT access token
 * @param {number} expiresIn - Expiration time in seconds (optional)
 * @returns {number} Timestamp when session expires
 */
export const calculateExpirationTime = (token, expiresIn = null) => {
  try {
    if (expiresIn) {
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + expiresIn * 1000);
      return expirationDate.getTime();
    }

    // Try to get expiration from token
    const decoded = decodeToken(token);
    if (decoded && decoded.exp) {
      return decoded.exp * 1000; // Convert to milliseconds
    }

    // Default: 8 hours from now
    const defaultExpiration = new Date();
    defaultExpiration.setHours(defaultExpiration.getHours() + 8);
    return defaultExpiration.getTime();
  } catch (error) {
    console.error("Error calculating expiration time:", error);
    // Default: 8 hours from now
    const defaultExpiration = new Date();
    defaultExpiration.setHours(defaultExpiration.getHours() + 8);
    return defaultExpiration.getTime();
  }
};

/**
 * Maps platform_role to application roles
 * @param {string} platformRole - Platform role from cookie (e.g., "ADMIN")
 * @returns {Array} Array of role strings matching application constants
 */
const mapPlatformRoleToAppRoles = (platformRole) => {
  if (!platformRole) return [];

  // Map platform roles to application role constants
  const roleMap = {
    ADMIN: [constants.ROLES.HO_ADMIN, constants.ROLES.DEVELOPER],
    HUB_ADMIN: [constants.ROLES.HUB_ADMIN],
    CP_ADMIN: [constants.ROLES.CP_ADMIN],
    MD: [constants.ROLES.ROLE_MD],
    ZONAL_HEAD: [constants.ROLES.ROLE_ZONALHEAD],
    ZONAL_OPERATOR: [constants.ROLES.ROLE_ZONALOPERATOR],
    RM: [constants.ROLES.ROLE_RM],
    FUNCTIONAL_HEAD: [constants.ROLES.ROLE_FUNCTIONALHEAD],
    CENTRAL_OPERATOR: [constants.ROLES.ROLE_CENTRALOPERATOR],
    HUB_OPERATOR: [constants.ROLES.ROLE_HUBOPERATOR],
    // Add more mappings as needed
  };

  return roleMap[platformRole] || [];
};

/**
 * Sets up localStorage with user information from cookies
 * @param {Object} tokenData - Token data from cookies (id_token, user_email, platform_role, etc.)
 * @param {Object} additionalData - Additional user data (optional)
 * @returns {Object} User info object
 */
export const setupLocalStorageFromCookies = (tokenData, additionalData = {}) => {
  try {
    const accessToken = tokenData.id_token || tokenData.accessToken || tokenData.name;
    const username = tokenData.email || tokenData.username;

    if (!accessToken || !username) {
      throw new Error("Missing token or email in cookie data");
    }

    // Decode token to get user info
    const decodedToken = decodeToken(accessToken);

    // Map platform_role to application roles
    const platformRole = tokenData.platform_role;
    const mappedRoles = mapPlatformRoleToAppRoles(platformRole);

    // Try to get roles from decoded token first (most reliable)
    // Token might have roles in various formats: roles, role, authorities, etc.
    let roles = [];
    if (decodedToken) {
      // Handle authorities array format: [{"authority":"ROLE_HUBADMIN"}]
      if (decodedToken.authorities && Array.isArray(decodedToken.authorities)) {
        roles = decodedToken.authorities
          .map(auth => auth.authority || auth)
          .filter(Boolean);
      } else if (decodedToken.roles) {
        roles = Array.isArray(decodedToken.roles) ? decodedToken.roles : [decodedToken.roles];
      } else if (decodedToken.role) {
        roles = Array.isArray(decodedToken.role) ? decodedToken.role : [decodedToken.role];
      }
    }

    // If no roles in token, use mapped roles from platform_role cookie
    if (!roles || roles.length === 0) {
      roles = mappedRoles;
    }

    // Fallback to additionalData roles if still empty
    if ((!roles || roles.length === 0) && additionalData?.roles) {
      roles = additionalData.roles;
    }

    // Extract token timestamps
    const expiryTime = decodedToken?.exp ? decodedToken.exp * 1000 : null;
    const issuedTime = decodedToken?.iat ? decodedToken.iat * 1000 : null;
    const expiresIn = decodedToken?.exp
      ? Math.floor((decodedToken.exp * 1000 - Date.now()) / 1000)
      : 28800; // Default 8 hours

    // Create userinfo object similar to login response
    const userinfo = {
      accessToken,
      username,
      expiresIn,
      expiryTime,
      issuedTime,
      roles,
      clientName: null,
      hubType: null,
      premiseId: null,
      userId: null,
      platform_role: platformRole,
      refresh_token: tokenData.refresh_token,
      token_type: tokenData.token_type || "Bearer",
      ...additionalData,
    };

    // Set localStorage items
    localStorage.setItem("userinfo", JSON.stringify(userinfo));

    // Calculate and set expiration time
    const expirationTime = calculateExpirationTime(accessToken, userinfo.expiresIn);
    localStorage.setItem("loggedOutTime", JSON.stringify(expirationTime));

    return userinfo;
  } catch (error) {
    console.error("Error setting up localStorage from cookies:", error);
    throw error;
  }
};

/**
 * Checks if user is authenticated based on cookies and localStorage
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  try {
    // Check if we have required localStorage items
    const loggedOutTime = localStorage.getItem("loggedOutTime");
    const userinfo = localStorage.getItem("userinfo");

    if (!loggedOutTime || !userinfo) {
      return false;
    }

    // 2. Check if session has expired via timestamp in localStorage
    const expirationTime = JSON.parse(loggedOutTime);
    if (Date.now() >= expirationTime) {
      // Session expired, clear storage
      clearAuthData();
      return false;
    }

    // 3. ALSO check if cookies are still present (Source of Truth)
    // This allows syncing login/logout across multiple apps on the same domain
    const tokenData = readAuthCookies();
    if (!tokenData || (!tokenData.id_token && !tokenData.accessToken)) {
      // Cookies missing - was logged out elsewhere
      clearAuthData();
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Clears all authentication data including cookies
 */
export const clearAuthData = () => {
  try {
    // 1. Clear Local Storage
    localStorage.clear();

    // 2. Clear Session Storage
    sessionStorage.clear();

    // 3. Clear Auth Cookies
    const cookiesToClear = ["userToken", "id_token", "user_email", "platform_role", "refresh_token", "token_type"];
    const domains = [window.location.hostname, ".innofulfill.com"];
    const expires = "Thu, 01 Jan 1970 00:00:00 GMT";

    cookiesToClear.forEach(cookieName => {
      domains.forEach(domain => {
        try {
          // Clear for specific domain and root path
          document.cookie = `${cookieName}=; path=/; domain=${domain}; expires=${expires}`;
          // Also try without domain for current hostname
          document.cookie = `${cookieName}=; path=/; expires=${expires}`;
        } catch (e) {
          console.warn(`Failed to clear cookie ${cookieName} for domain ${domain}`, e);
        }
      });
    });
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

/**
 * Logout function - clears auth data and redirects to configured logout URL
 */
export const logout = () => {
  clearAuthData();
  // Redirect using static constant value
  const logoutUrl = constants.LOGOUT_REDIRECT_URL;
  window.location.href = logoutUrl;
};

/**
 * Main function to initialize authentication from cookies
 * This should be called on app load to check for existing session
 * @returns {Object|null} User info if authenticated, null otherwise
 */
export const initializeAuthFromCookies = () => {
  try {
    // 1. ALWAYS Try to read from cookies first (handles localhost fallback internally)
    // This is the source of truth for the domain-level session
    const tokenData = readAuthCookies();

    if (!tokenData || (!tokenData.id_token && !tokenData.accessToken) || (!tokenData.email && !tokenData.username)) {
      // If no valid cookie/fallback data found, clear any leftover local state
      if (localStorage.getItem("userinfo") || localStorage.getItem("userId")) {
        clearAuthData();
      }
      return null;
    }

    // 2. If cookies are valid, always setup/refresh localStorage to stay in sync
    const userinfo = setupLocalStorageFromCookies(tokenData);

    return userinfo;
  } catch (error) {
    console.error("Error initializing auth from cookies:", error);
    return null;
  }
};

/**
 * Gets the redirect path based on user roles
 * @param {Object} userinfo - User info object with roles
 * @returns {string} Redirect path
 */
export const getRedirectPath = (userinfo) => {
  try {
    const userRoles = userinfo?.roles || [];

    // Check for saved redirect URL
    const savedUrl = localStorage.getItem("viewClient");
    if (savedUrl && savedUrl !== "/") {
      return savedUrl;
    }

    // Check for ROLE_MD and redirect to Director Dashboard
    if (userRoles.includes(constants.ROLES.ROLE_MD)) {
      return "/director-dashboardNew2";
    }

    // Default to dashboard
    return "/dashboard";
  } catch (error) {
    console.error("Error getting redirect path:", error);
    return "/dashboard";
  }
};

/**
 * Gets user email from cookies or localStorage
 * @returns {string|null} User email or null
 */
export const getUserEmailFromCookies = () => {
  try {
    const tokenData = readAuthCookies();
    if (tokenData && (tokenData.email || tokenData.username)) {
      return tokenData.email || tokenData.username;
    }

    // Fallback to localStorage
    const userinfo = localStorage.getItem("userinfo");
    if (userinfo) {
      const parsed = JSON.parse(userinfo);
      return parsed?.username || null;
    }

    return null;
  } catch (error) {
    console.error("Error getting user email:", error);
    return null;
  }
};

/**
 * Gets access token from cookies or localStorage
 * @returns {string|null} Access token or null
 */
export const getAccessTokenFromCookies = () => {
  try {
    const tokenData = readAuthCookies();
    if (tokenData && (tokenData.id_token || tokenData.accessToken || tokenData.name)) {
      return tokenData.id_token || tokenData.accessToken || tokenData.name;
    }

    // Fallback to localStorage
    const userinfo = localStorage.getItem("userinfo");
    if (userinfo) {
      const parsed = JSON.parse(userinfo);
      return parsed?.accessToken || null;
    }

    return null;
  } catch (error) {
    console.error("Error getting access token:", error);
    return null;
  }
};

/**
 * Updates userinfo in localStorage with account service data
 * @param {Object} accountResult - Account service response data (with data.userInfo and data.premiseInfo)
 * @returns {Object} Updated userinfo object
 */
export const updateUserinfoWithAccountData = (accountResult) => {
  try {
    const userinfoStr = localStorage.getItem("userinfo");
    if (!userinfoStr) {
      console.warn("No userinfo found in localStorage to update");
      return null;
    }

    const userinfo = JSON.parse(userinfoStr);

    // Extract data from accountResult - response structure: { success, message, data: { userInfo, premiseInfo } }
    const data = accountResult?.data || accountResult || {};
    const userInfo = data?.userInfo || {};
    const premiseInfo = data?.premiseInfo || {};

    // Update userinfo with account service data
    const updatedUserinfo = {
      ...userinfo,
      userId: userInfo.userId || userinfo.userId || null,
      premiseId: userInfo.premiseId || premiseInfo.premiseId || userinfo.premiseId || null,
      hubType: premiseInfo.hubType || userinfo.hubType || null,
      clientName: userInfo.clientName || premiseInfo.clientName || userinfo.clientName || null,
      // Update roles from account service response if available
      roles: userInfo.roles && Array.isArray(userInfo.roles) && userInfo.roles.length > 0
        ? userInfo.roles
        : userinfo.roles || [],
    };

    // Save updated userinfo back to localStorage
    localStorage.setItem("userinfo", JSON.stringify(updatedUserinfo));

    return updatedUserinfo;
  } catch (error) {
    console.error("Error updating userinfo with account data:", error);
    return null;
  }
};

/**
 * Fetches account service details using cookie-based authentication
 * @param {string} email - User email address
 * @returns {Promise<Object>} Account service response data
 * Response structure: { success: true, message: "...", data: { userInfo: {...}, premiseInfo: {...} } }
 */
export const getAccountServiceByCookies = async (email) => {
  try {
    if (!email) {
      throw new Error("Email is required");
    }

    // Build the API URL
    const url = `${APIs.baseURL}/account-service/v1/details/${email}`;

    // Make GET request with cookies (no Authorization header needed)
    const response = await axios.get(url, {
      //   withCredentials: true, // Include cookies in the request
      //   headers: {
      //     "Content-Type": "application/json",
      //     Accept: "application/json",
      //   },
    });

    // Response structure: { success: true, message: "...", data: { userInfo: {...}, premiseInfo: {...} } }
    if (response?.data?.success && response?.data?.data) {
      return response.data;
    }

    throw new Error(response?.data?.message || "Invalid response from account service");
  } catch (error) {
    console.error("Error fetching account service by cookies:", error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

